import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { logoutAsync } from "../api/AccountApi";
import { getUserAsync } from "../api/UserApi";
import { getOrganizationByIdAsync } from "../api/OrganizationApi";
import { OrganizationResponse } from "../models/OrganizationModels";
import { SystemRole } from "../models/User";
import {
  OrganizationMembership,
  Privilege,
  PrivilegeAction,
  UserProfileResponse,
} from "../models/UserProfileResponse";
import { UserRole } from "../config/navigation";
import { RoleType } from "../models/UserModels";
import { Constants } from "../constants/constants";

export interface AuthUser extends UserProfileResponse {
  role: UserRole;
  isPlatformOwner: boolean;
  activeOrganization: OrganizationMembership | null;
  privileges: Privilege[];
}

export interface UseAuthReturn {
  user: AuthUser | null;
  loading: boolean;
  organizations: OrganizationResponse[];
  selectOrganization: (organizationId: string) => void;
  selectPlatformOrganization: (org: OrganizationResponse | null) => void;
  hasPrivilege: (form: number, action?: PrivilegeAction) => boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<UseAuthReturn | undefined>(undefined);

const toUserRole = (roleType: RoleType | string): UserRole => {
  const name = typeof roleType === "number" ? RoleType[roleType] : roleType;
  switch (name) {
    case "PlatformOwner":
    case "OrganizationOwner":
    case "Admin":
    case "Manager":
    case "Receptionist":
    case "Staff":
      return name;
    default:
      return "Staff";
  }
};

const resolveActiveOrganization = (
  organizations: OrganizationMembership[],
  savedId: string | null,
): OrganizationMembership | null => {
  if (organizations.length === 0) return null;
  return (
    organizations.find(
      (org) => org.organizationId === savedId || org.id === savedId,
    ) ??
    organizations.find((org) => org.isDefault) ??
    organizations[0]
  );
};

const FULL_ACCESS_PRIVILEGES: Privilege[] = Array.from(
  { length: 10 },
  (_, i) => ({
    form: i + 1,
    privilegeValue: 15,
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canView: true,
  }),
);

const synthesizePlatformMembership = (
  org: OrganizationResponse,
): OrganizationMembership => ({
  ...org,
  organizationId: org.id,
  roleType: RoleType.PlatformOwner,
  roleName: "Platform Owner",
  role: {
    roleType: RoleType.PlatformOwner,
    name: "Platform Owner",
    privileges: FULL_ACCESS_PRIVILEGES,
  },
  isDefault: false,
  membershipStatus: "Active",
  invitationExpires: null,
  branches: null,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const inFlightRef = useRef<Promise<void> | null>(null);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    // Single-flight: concurrent loadUser calls (e.g. StrictMode remount,
    // multiple consumers) share one in-flight request instead of N.
    if (inFlightRef.current) return inFlightRef.current;

    inFlightRef.current = (async () => {
      try {
        const res = await getUserAsync(token);

        if (res.success && res.result) {
          const profile = res.result;
          const isPlatformOwner =
            profile.systemRole === SystemRole.PlatformOwner;

          let role: UserRole = "Staff";
          let activeOrganization: OrganizationMembership | null = null;
          let privileges: Privilege[] = [];

          if (isPlatformOwner) {
            role = "PlatformOwner";
            const savedOrgId = localStorage.getItem(
              Constants.ACTIVE_ORGANIZATION,
            );
            if (savedOrgId) {
              try {
                const orgRes = await getOrganizationByIdAsync(
                  token,
                  savedOrgId,
                );
                const org = orgRes.success ? orgRes.result : null;
                if (org) {
                  activeOrganization = synthesizePlatformMembership(org);
                  privileges = FULL_ACCESS_PRIVILEGES;
                } else {
                  localStorage.removeItem(Constants.ACTIVE_ORGANIZATION);
                }
              } catch (error) {
                console.error("Failed to restore active organization:", error);
                localStorage.removeItem(Constants.ACTIVE_ORGANIZATION);
              }
            }
          } else {
            const savedOrgId = localStorage.getItem(
              Constants.ACTIVE_ORGANIZATION,
            );
            activeOrganization = resolveActiveOrganization(
              profile.organizations ?? [],
              savedOrgId,
            );
            role = activeOrganization
              ? toUserRole(activeOrganization.roleType)
              : "Staff";
            privileges = activeOrganization?.role.privileges ?? [];
          }

          const authUser: AuthUser = {
            ...profile,
            role,
            isPlatformOwner,
            activeOrganization,
            privileges,
          };

          setUser(authUser);
          localStorage.setItem(Constants.USER_INFO, JSON.stringify(authUser));
          if (activeOrganization) {
            localStorage.setItem(
              Constants.ACTIVE_ORGANIZATION,
              activeOrganization.organizationId,
            );
          }
        } else {
          setUser(null);
          localStorage.removeItem(Constants.ACCESS_TOKEN);
          localStorage.removeItem(Constants.USER_INFO);
        }
      } catch (error) {
        console.error("Failed to load user:", error);
        setUser(null);
        localStorage.removeItem(Constants.ACCESS_TOKEN);
        localStorage.removeItem(Constants.USER_INFO);
      } finally {
        inFlightRef.current = null;
        setLoading(false);
      }
    })();

    return inFlightRef.current;
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const selectOrganization = useCallback(
    (organizationId: string) => {
      setUser((prev) => {
        if (!prev || prev.isPlatformOwner) return prev;

        const organization =
          prev.organizations.find(
            (org) =>
              org.organizationId === organizationId ||
              org.id === organizationId,
          ) ?? null;
        if (!organization) return prev;

        const next: AuthUser = {
          ...prev,
          role: toUserRole(organization.roleType),
          activeOrganization: organization,
          privileges: organization.role.privileges,
        };

        localStorage.setItem(Constants.USER_INFO, JSON.stringify(next));
        localStorage.setItem(
          Constants.ACTIVE_ORGANIZATION,
          organization.organizationId,
        );
        return next;
      });
    },
    [],
  );

  const selectPlatformOrganization = useCallback(
    (org: OrganizationResponse | null) => {
      setUser((prev) => {
        if (!prev || !prev.isPlatformOwner) return prev;

        if (!org) {
          const cleared: AuthUser = {
            ...prev,
            activeOrganization: null,
            privileges: [],
          };
          localStorage.removeItem(Constants.ACTIVE_ORGANIZATION);
          localStorage.setItem(Constants.USER_INFO, JSON.stringify(cleared));
          return cleared;
        }

        const next: AuthUser = {
          ...prev,
          activeOrganization: synthesizePlatformMembership(org),
          privileges: FULL_ACCESS_PRIVILEGES,
        };
        localStorage.setItem(Constants.ACTIVE_ORGANIZATION, org.id);
        localStorage.setItem(Constants.USER_INFO, JSON.stringify(next));
        return next;
      });
    },
    [],
  );

  const hasPrivilege = useCallback(
    (form: number, action: PrivilegeAction = "canView"): boolean => {
      if (!user) return false;
      if (user.isPlatformOwner) return true;
      const privilege = user.privileges.find((p) => p.form === form);
      return privilege ? privilege[action] === true : false;
    },
    [user],
  );

  const logout = async () => {
    try {
      await logoutAsync();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem(Constants.ACCESS_TOKEN);
      localStorage.removeItem(Constants.USER_INFO);
      localStorage.removeItem(Constants.ACTIVE_ORGANIZATION);
      setUser(null);
      navigate("/login");
    }
  };

  const refreshUser = async () => {
    setLoading(true);
    await loadUser();
  };

  const value: UseAuthReturn = {
    user,
    loading,
    organizations: user?.organizations ?? [],
    selectOrganization,
    selectPlatformOrganization,
    hasPrivilege,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): UseAuthReturn => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};